import { useState } from "react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  address: "",
  city: "",
  state: "",
  zip: "",
};

export default function CheckoutDialog({ open, onOpenChange }) {
  const { items, subtotal } = useCart();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast("Your bag is empty");
      return;
    }
    if (!form.name || !form.email || !form.address || !form.city || !form.zip) {
      toast("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
        customer_email: form.email,
        customer_name: form.name,
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_state: form.state,
        shipping_zip: form.zip,
      };

      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error(err);
      toast.error("Checkout failed", {
        description: err?.response?.data?.detail || err.message,
      });
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white text-black border border-black/10 rounded-none max-w-lg p-0 gap-0"
        data-testid="checkout-dialog"
      >
        <DialogHeader className="px-6 pt-8 pb-2">
          <DialogTitle className="text-2xl font-black tracking-tight uppercase">
            Checkout
          </DialogTitle>
          <DialogDescription className="text-[11px] tracking-[0.25em] uppercase text-black/50">
            Shipping & contact — card on next step
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 pb-6" data-testid="checkout-form">
          <div className="grid grid-cols-1 gap-1 pt-4">
            <input
              className="meao-input"
              type="text"
              name="name"
              placeholder="Full name"
              value={form.name}
              onChange={handleChange}
              required
              data-testid="checkout-name"
            />
            <input
              className="meao-input"
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              required
              data-testid="checkout-email"
            />
            <input
              className="meao-input"
              type="text"
              name="address"
              placeholder="Street address"
              value={form.address}
              onChange={handleChange}
              required
              data-testid="checkout-address"
            />
            <div className="grid grid-cols-3 gap-3">
              <input
                className="meao-input col-span-1"
                type="text"
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
                required
                data-testid="checkout-city"
              />
              <input
                className="meao-input col-span-1"
                type="text"
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
                data-testid="checkout-state"
              />
              <input
                className="meao-input col-span-1"
                type="text"
                name="zip"
                placeholder="ZIP"
                value={form.zip}
                onChange={handleChange}
                required
                data-testid="checkout-zip"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-between items-center border-t border-black/10 pt-4">
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-black/60">
              Total
            </span>
            <span className="text-xl font-black" data-testid="checkout-total">
              ${subtotal.toFixed(2)}
            </span>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="meao-btn w-full mt-6"
            data-testid="checkout-submit"
          >
            {submitting ? "Redirecting…" : "Continue to Payment"}
          </button>
          <p className="text-[10px] tracking-[0.2em] uppercase text-black/40 mt-3 text-center">
            Secure card payment via Stripe
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
